import { RawParams, StateOrName, TransitionOptions } from "@uirouter/angular";
import { Component } from "@angular/core";
import { take, takeUntil } from "rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import {
  NAVIGATION_COLOR_STEP,
  NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS,
  SETTINGS_STEP,
  STILL_HAVE_QUESTIONS_STEP,
} from "@webapp/navigation/models/navigation-onboarding.models";
import { NavigationOnboardingService } from "@webapp/navigation/services/navigation-onboarding.service";
import { SettingsSubNavStateService } from "@webapp/navigation/services/settings/settings-sub-nav-state.service";
import { IOnboardingDefinition, IOnboardingDelegate, IOnboardingJourneyState } from "@webapp/shared/components/onboarding/onboarding.models";
import { ProfileTheme, ProfileThemeService } from "../services/profile-theme.service";

@Component({
  selector: "profile-theme",
  templateUrl: "./profile-theme.component.html",
  styleUrls: ["./profile-theme.component.less"],
})
export class ProfileThemeComponent implements IOnboardingDelegate {
  public profileTheme: string;

  public onboarding: IOnboardingDefinition = { shouldShow: false, totalSteps: 0, previewOverrides: { totalSteps: NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS } };
  private skipOnboardingSteps: number[];
  private settingsState: string;

  constructor(
    private navigationOnboardingService: NavigationOnboardingService,
    private destroyService$: DestroyService,
    protected profileThemeService: ProfileThemeService,
    private settingsSubNavStateService: SettingsSubNavStateService
  ) {
    this.profileTheme = this.profileThemeService.getProfileThemeCurrentValue();

    this.navigationOnboardingService
      .getState$()
      .pipe(takeUntil(this.destroyService$))
      .subscribe((state: IOnboardingJourneyState) => {
        this.onboarding = { shouldShow: false, totalSteps: 0, delegate: undefined, previewOverrides: { totalSteps: NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS } };
        if (state.stage === "ONGOING" && state.currentStep === NAVIGATION_COLOR_STEP) {
          this.onboarding.startingStep = NAVIGATION_COLOR_STEP;
          this.skipOnboardingSteps = state.skipSteps || [];
          if (this.skipOnboardingSteps.includes(SETTINGS_STEP)) {
            this.onboarding.previewOverrides.stepsMap = {
              [NAVIGATION_COLOR_STEP]: SETTINGS_STEP,
              [STILL_HAVE_QUESTIONS_STEP]: NAVIGATION_COLOR_STEP,
            };
          }
          this.onboarding.totalSteps = NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS;
          this.onboarding.shouldShow = true;
          this.onboarding.delegate = this;
          this.onboarding.previewOverrides.totalSteps = state.totalSteps;
        }
        if (state.stage === "ENDED_BY_USER") {
          this.onboarding.shouldShow = false;
        }
      });
    this.settingsSubNavStateService
      .firstVisibleState$()
      .pipe(take(1))
      .subscribe((settingsState) => {
        this.settingsState = settingsState;
      });
  }

  public shouldSeeOnboarding(): boolean {
    return this.onboarding.shouldShow;
  }

  public getRedirectStateForStep(step: number): { to: StateOrName; params?: RawParams; options?: TransitionOptions } {
    if (step === SETTINGS_STEP) {
      if (this.skipOnboardingSteps.includes(SETTINGS_STEP)) {
        this.navigationOnboardingService.moveBackward({ inTransition: true, stepsDelta: 2 });
        return { to: "gtmhub.lists" };
      } else {
        this.navigationOnboardingService.moveBackward({ inTransition: true });
        return { to: this.settingsState || "gtmhub.configuration.methodology" };
      }
    }
    if (step === STILL_HAVE_QUESTIONS_STEP) {
      this.navigationOnboardingService.moveForward({ inTransition: true });
      return { to: "gtmhub.home" };
    }
  }

  public themeChanged(newTheme: ProfileTheme): void {
    this.profileThemeService.setProfileTheme(newTheme);
  }
}
